/**
 * CameraPlayer — reusable Vue 2 HLS live-video component for the admin dashboard.
 *
 * Backend contract (admin):
 *   GET /api/Cameras/Trip/{tripId}/Live
 *     → { PlaybackUrl: "https://media.../live/{streamId}/index.m3u8",
 *         Token, ExpiresAt, StreamId }
 *
 * Props:
 *   tripId        (Number|String, required if not directly given a playbackUrl)
 *                 — Trip whose live camera stream we want to view.
 *   cameraId      (Number|String, optional) — Forensic/audit label only; the
 *                 backend keys playback off TripId, not CameraDevice.Id.
 *   playbackUrl   (String, optional)        — If the caller already has a
 *                 short-lived m3u8 URL, skip the token-fetch step and just
 *                 play it. The list page uses this to avoid duplicate calls.
 *   autoPlay      (Boolean, default true)
 *   muted         (Boolean, default true)  — Required for autoplay on Chrome.
 *   compact       (Boolean, default false) — 4:3 layout for grid tiles.
 *   refreshMs     (Number,  default 5000)  — Retry interval on error.
 *
 * Behaviour:
 *   - On mount (or prop change) we fetch the live playlist URL.
 *   - hls.js is dynamically loaded so the dependency isn't pulled into
 *     every page bundle (only the Cameras + LiveTracking screens need it).
 *   - On native HLS (Safari/iOS) we skip hls.js and feed the <video> directly.
 *   - Any failure shows "Stream offline" and auto-retries every `refreshMs`.
 *   - We surface a `state` event so parents can render their own status pills.
 */
import axios from 'axios';

const STATE_IDLE     = 'idle';
const STATE_LOADING  = 'loading';
const STATE_PLAYING  = 'playing';
const STATE_OFFLINE  = 'offline';
const STATE_ENDED    = 'ended';
// Cameras have not been imported/deployed yet (owner decision). Until the
// media server exists, hitting /Cameras/Trip/{id}/Live just 404s and the
// player retries forever. When the global flag is not explicitly enabled we
// short-circuit to a calm "coming soon" placeholder instead — no network call,
// no retry loop. Flip `window.RAFIQ_CAMERAS_ENABLED = true` once the camera
// media pipeline is live to restore real playback.
const STATE_DISABLED = 'disabled';

// hls.js is heavy (~120 KB gzipped). Lazy-import so screens that never open
// the player don't pay the cost. Cached promise — first caller wins.
let _hlsModulePromise = null;
function loadHlsLib() {
    if (!_hlsModulePromise) {
        _hlsModulePromise = import(/* webpackChunkName: "hlsjs" */ 'hls.js')
            .then(m => m.default || m)
            .catch(err => {
                // Reset the cache so a later retry can try again rather than
                // being stuck with a poisoned rejection.
                _hlsModulePromise = null;
                throw err;
            });
    }
    return _hlsModulePromise;
}

export default {
    name: 'CameraPlayer',

    props: {
        tripId:       { type: [Number, String], default: null },
        cameraId:     { type: [Number, String], default: null },
        playbackUrl:  { type: String, default: '' },
        autoPlay:     { type: Boolean, default: true },
        muted:        { type: Boolean, default: true },
        compact:      { type: Boolean, default: false },
        refreshMs:    { type: Number, default: 5000 },
        title:        { type: String, default: '' },
    },

    data() {
        return {
            state: STATE_IDLE,        // 'idle' | 'loading' | 'playing' | 'offline' | 'ended'
            errorMessage: '',
            retryCount: 0,
            currentUrl: '',
            tokenExpiresAt: null,
        };
    },

    computed: {
        isPlaying()      { return this.state === STATE_PLAYING; },
        isLoading()      { return this.state === STATE_LOADING || this.state === STATE_IDLE; },
        isOffline()      { return this.state === STATE_OFFLINE; },
        isEnded()        { return this.state === STATE_ENDED; },
        isDisabled()     { return this.state === STATE_DISABLED; },
        // Real playback is attempted only when the camera pipeline is explicitly
        // enabled OR the caller already handed us a concrete playback URL.
        camerasEnabled() {
            return (typeof window !== 'undefined' && window.RAFIQ_CAMERAS_ENABLED === true)
                || !!(this.playbackUrl && this.playbackUrl.trim());
        },
        labelTripId()    { return this.tripId != null ? `#${this.tripId}` : ''; },
        labelCameraId()  { return this.cameraId != null ? `كاميرا #${this.cameraId}` : ''; },
    },

    watch: {
        tripId()      { this.reloadStream(); },
        playbackUrl() { this.reloadStream(); },
    },

    created() {
        // Non-reactive imperative refs — see LiveTracking.js for the same
        // pattern (Vue 2 strips keys starting with `_`/`$` from data()).
        this.hlsInstance = null;
        this.retryTimer  = null;
    },

    mounted() {
        this.reloadStream();
    },

    beforeDestroy() {
        this.teardown();
    },

    methods: {

        // ============ Lifecycle ============

        teardown() {
            this.clearRetry();
            this.destroyHls();
            const video = this.$refs.video;
            if (video) {
                this.unbindNativeListeners(video);
                try {
                    video.pause();
                    video.removeAttribute('src');
                    video.load();
                } catch (_) { /* noop */ }
            }
        },

        destroyHls() {
            if (this.hlsInstance) {
                try { this.hlsInstance.destroy(); } catch (_) { /* noop */ }
                this.hlsInstance = null;
            }
        },

        clearRetry() {
            if (this.retryTimer) {
                clearTimeout(this.retryTimer);
                this.retryTimer = null;
            }
        },

        scheduleRetry(reason) {
            this.clearRetry();
            this.state = STATE_OFFLINE;
            this.errorMessage = reason || 'البث غير متاح';
            this.$emit('state', { state: this.state, reason: this.errorMessage });
            this.retryTimer = setTimeout(() => {
                this.retryCount += 1;
                this.reloadStream(/*manualRetry*/ false);
            }, Math.max(1500, this.refreshMs));
        },

        async reloadStream(manualRetry = true) {
            this.clearRetry();
            this.destroyHls();

            // Camera pipeline not yet deployed → show the "coming soon" placeholder
            // and make NO request (avoids the perpetual 404 retry loop).
            if (!this.camerasEnabled) {
                this.state = STATE_DISABLED;
                this.errorMessage = '';
                this.$emit('state', { state: this.state });
                return;
            }

            this.state = STATE_LOADING;
            this.errorMessage = '';
            this.$emit('state', { state: this.state });

            if (manualRetry) this.retryCount = 0;

            // 1) Resolve the m3u8 URL — either provided up-front or fetched.
            let url = (this.playbackUrl || '').trim();

            if (!url) {
                if (this.tripId == null || this.tripId === '') {
                    this.scheduleRetry('لم يتم تحديد رحلة');
                    return;
                }
                try {
                    const res = await axios.get(`/api/Cameras/Trip/${this.tripId}/Live`, { withCredentials: true });
                    const data = res && res.data ? res.data : {};
                    url = data.PlaybackUrl || data.playbackUrl || '';
                    this.tokenExpiresAt = data.ExpiresAt || data.expiresAt || null;
                    if (!url) {
                        this.scheduleRetry('لم يُرجع الخادم رابط بث');
                        return;
                    }
                } catch (err) {
                    const code = err && err.response && err.response.status;
                    if (code === 404) {
                        this.scheduleRetry('لا يوجد بث مباشر لهذه الرحلة حالياً');
                    } else if (code === 403) {
                        // 403 is a hard stop — don't auto-retry, the user has no rights.
                        this.state = STATE_OFFLINE;
                        this.errorMessage = 'لا تملك صلاحية مشاهدة هذا البث';
                        this.$emit('state', { state: this.state, reason: this.errorMessage });
                        return;
                    } else if (code === 410) {
                        this.state = STATE_ENDED;
                        this.errorMessage = 'انتهت صلاحية البث';
                        this.$emit('state', { state: this.state, reason: this.errorMessage });
                        return;
                    } else {
                        this.scheduleRetry('تعذّر الوصول إلى خدمة البث');
                    }
                    return;
                }
            }

            this.currentUrl = url;

            // 2) Attach the URL to the <video> via hls.js OR native HLS.
            const video = this.$refs.video;
            if (!video) {
                // Component was torn down mid-fetch; nothing to do.
                return;
            }

            if (video.canPlayType('application/vnd.apple.mpegurl')) {
                // Safari / iOS native HLS — best path; no JS shim needed.
                video.src = url;
                this.bindNativeListeners(video);
                if (this.autoPlay) {
                    try { await video.play(); } catch (_) { /* user gesture may be required */ }
                }
                return;
            }

            // Everything else → hls.js
            try {
                const Hls = await loadHlsLib();
                if (!Hls.isSupported()) {
                    this.scheduleRetry('المتصفح لا يدعم HLS');
                    return;
                }

                const hls = new Hls({
                    enableWorker: true,
                    lowLatencyMode: true,
                    backBufferLength: 60,
                    liveSyncDurationCount: 3,
                    maxBufferLength: 10,
                });

                hls.loadSource(url);
                hls.attachMedia(video);

                hls.on(Hls.Events.MANIFEST_PARSED, () => {
                    this.state = STATE_PLAYING;
                    this.$emit('state', { state: this.state });
                    if (this.autoPlay) {
                        video.play().catch(() => { /* awaiting user gesture */ });
                    }
                });

                hls.on(Hls.Events.ERROR, (_evt, data) => {
                    if (!data) return;
                    if (data.fatal) {
                        switch (data.type) {
                            case Hls.ErrorTypes.NETWORK_ERROR:
                                this.destroyHls();
                                this.scheduleRetry('انقطاع الاتصال بالبث');
                                return;
                            case Hls.ErrorTypes.MEDIA_ERROR:
                                // Try to recover once before giving up.
                                try { hls.recoverMediaError(); return; } catch (_) { /* fallthrough */ }
                                this.destroyHls();
                                this.scheduleRetry('خطأ في تشغيل الفيديو');
                                return;
                            default:
                                this.destroyHls();
                                this.scheduleRetry('فشل تشغيل البث');
                                return;
                        }
                    }
                    // Non-fatal — log for diagnostics, hls.js will continue.
                    // eslint-disable-next-line no-console
                    console.warn('[CameraPlayer] non-fatal hls error', data.type, data.details);
                });

                this.hlsInstance = hls;
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('[CameraPlayer] hls.js load failed', err && err.message);
                this.scheduleRetry('تعذّر تحميل مشغّل HLS');
            }
        },

        bindNativeListeners(video) {
            // FE-04 fix (2026-05-24): this runs again on every stream reload/retry
            // against the SAME persistent <video> element. Detach any previous
            // handlers first and keep references so teardown() can remove them —
            // otherwise listeners accumulate for the lifetime of the component.
            this.unbindNativeListeners(video);
            const onPlaying = () => {
                this.state = STATE_PLAYING;
                this.$emit('state', { state: this.state });
            };
            const onError = () => {
                this.scheduleRetry('انقطاع البث');
            };
            const onEnded = () => {
                this.state = STATE_ENDED;
                this.$emit('state', { state: this.state });
            };
            video.addEventListener('playing', onPlaying, { once: false });
            video.addEventListener('error',   onError,   { once: false });
            video.addEventListener('ended',   onEnded,   { once: false });
            this.nativeHandlers = { onPlaying, onError, onEnded };
        },

        unbindNativeListeners(video) {
            if (!video || !this.nativeHandlers) return;
            video.removeEventListener('playing', this.nativeHandlers.onPlaying);
            video.removeEventListener('error',   this.nativeHandlers.onError);
            video.removeEventListener('ended',   this.nativeHandlers.onEnded);
            this.nativeHandlers = null;
        },

        // ============ Public API ============

        retryNow() {
            this.retryCount += 1;
            this.reloadStream(/*manualRetry*/ true);
        },
    }
};
